import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useFeatureFlagsContext } from "@/hooks/useFeatureFlag";
import { Button } from "@/components/ui/button";
import { ALWAYS_ON_KEYS } from "@/constants/featureRegistry";

/**
 * FeatureRouteGuard — blokir direct URL access ke halaman admin yang
 * feature-nya OFF untuk paket tenant saat ini.
 *
 * Semantik FAIL-CLOSED: key yang tidak dikenal / disabled → blocked.
 * Fail-open HANYA saat fetch feature flags gagal total (network outage) —
 * backend gate (403 FEATURE_NOT_AVAILABLE) tetap menjadi pertahanan akhir.
 */
export default function FeatureRouteGuard({ featureKey, children }) {
  const { features, loading, error, package: pkg } = useFeatureFlagsContext();

  if (!featureKey || ALWAYS_ON_KEYS.includes(featureKey)) return children;

  if (loading) {
    return (
      <div
        className="min-h-[50vh] grid place-items-center p-4"
        data-testid="feature-guard-loading"
      >
        <div className="text-sm text-[#5C5C5C] font-semibold animate-pulse">Memuat…</div>
      </div>
    );
  }

  if (error) return children;

  const f = features[featureKey];
  if (f && f.enabled) return children;

  return (
    <div
      className="min-h-[60vh] grid place-items-center p-4"
      style={{ backgroundColor: "var(--brand-background)" }}
      data-testid="feature-not-available"
    >
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E5E5] text-center max-w-md">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 border border-amber-200 grid place-items-center mb-4">
          <Lock size={20} className="text-amber-600" />
        </div>
        <h2 className="font-display text-2xl font-bold text-[#1A1A1A] mb-2">Fitur Tidak Tersedia</h2>
        <p className="text-[#5C5C5C] text-sm leading-relaxed mb-1">
          Fitur ini tidak termasuk dalam paket Anda saat ini
          {pkg ? <> (<span className="font-semibold">{pkg}</span>)</> : null}.
        </p>
        <p className="text-xs text-[#9E9E9E] font-mono mb-5" data-testid="feature-not-available-key">{featureKey}</p>
        <Link to="/admin/langganan">
          <Button className="brand-btn-primary rounded-xl" data-testid="feature-upgrade-btn">
            Upgrade Paket
          </Button>
        </Link>
      </div>
    </div>
  );
}
